/**
 * AdminDashboardPage - Phase 2C-3
 *
 * Admin dashboard with system stats and recent activity.
 * BSL Compliant: Factual data only, neutral colors.
 */

import { Link } from 'react-router-dom';
import { useAdminStats } from '../../hooks/useAdminStats';
import { useAuditLog } from '../../hooks/useAuditLog';
import { Card, CardHeader, CardTitle, CardContent, LoadingSpinner, Alert, StatCard, StatCardGrid } from '../../components/common';
import { AdminNav } from '../../components/admin';

export function AdminDashboardPage(): JSX.Element {
  const { stats, loading: statsLoading, error: statsError } = useAdminStats();
  const { entries: recentActivity, loading: activityLoading } = useAuditLog();

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      pool_created: 'Pool Created',
      pool_updated: 'Pool Updated',
      pool_status_changed: 'Status Changed',
      pool_locked: 'Pool Locked',
      balance_adjusted: 'Balance Adjusted',
      member_removed: 'Member Removed',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0">
        <div className="sticky top-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            Admin
          </h2>
          <AdminNav />
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Admin Dashboard</h1>
          <p className="text-slate-400 mt-1">System overview and recent activity</p>
        </div>

        {/* Stats */}
        {statsError && (
          <Alert variant="error">{statsError}</Alert>
        )}

        {statsLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : stats ? (
          <StatCardGrid columns={4}>
            <StatCard
              label="Total Pools"
              value={stats.total_pools}
              subtitle={`${stats.active_pools} active`}
            />
            <StatCard
              label="Pool Status"
              value={stats.active_pools}
              subtitle={`${stats.pending_pools} pending, ${stats.locked_pools} locked`}
            />
            <StatCard
              label="Total Members"
              value={stats.total_memberships}
              subtitle={`${stats.total_users} unique users`}
            />
            <StatCard
              label="Completed"
              value={stats.completed_pools}
              subtitle="pools finished"
            />
          </StatCardGrid>
        ) : null}

        {/* Quick Links */}
        <div className="grid grid-cols-3 gap-4">
          <Link to="/admin/pools">
            <Card className="hover:bg-slate-800/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">Manage Pools</p>
                    <p className="text-sm text-slate-400">Create, edit, lock pools</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/members">
            <Card className="hover:bg-slate-800/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">Manage Members</p>
                    <p className="text-sm text-slate-400">Balances, removals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/audit">
            <Card className="hover:bg-slate-800/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">Audit Log</p>
                    <p className="text-sm text-slate-400">View all admin actions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <Link
                to="/admin/audit"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                View all →
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : recentActivity.length === 0 ? (
              <p className="text-center py-8 text-slate-400">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.slice(0, 10).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                        <span className="text-xs font-medium text-slate-300">
                          {entry.admin_user_id.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-slate-200">
                          <span className="font-medium">{entry.admin_user_id}</span>
                          {' '}
                          <span className="text-slate-400">{getActionLabel(entry.action)}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                          {entry.target_type}: {entry.target_id}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default AdminDashboardPage;
